import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { API_URL } from "../../config";
import Notification from "../../components/Notification.js";
import ConfirmDialog from "../../components/ConfirmDialog.js";
import { allowedToView } from "../../utils/PermissionUtils.js";
import { formatToDayMonthYear } from "../../utils/TextUtils.js";

const DebtRepaymentView = () => {
    const { id } = useParams();
    const [repayment, setRepayment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    
    const token = localStorage.getItem("access_token");
    const navigate = useNavigate();   
    const userRole = localStorage.getItem("userRole");
    
    const canCancel = (userRole) => {
        return allowedToView(userRole, ['admin', 'superadmin']);
    };

    useEffect(() => {
        const fetchRepayment = async () => {
            try {
                const response = await fetch(`${API_URL}/debt-repayments/${id}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setRepayment(data.data);
                } else if (response.status === 401) {
                    navigate("/logout");
                } else {
                    const errorData = await response.json();
                    setError(errorData.message || "An error occurred.");
                    setNotification({ 
                        message: errorData.message || "An error occurred.", 
                        type: "danger" 
                    });
                }

            } catch (error) {
                setError(error.message);
                setNotification({ 
                    message: "An error occurred while fetching repayment details.", 
                    type: "danger" 
                });
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchRepayment();
        }
    }, [id, token, navigate]);

    const handleCancelRepayment = () => {
        setIsConfirmDialogOpen(true);
    };

    const confirmCancelRepayment = async () => {
        setIsCancelling(true);
        try {
            const response = await fetch(`${API_URL}/debt-repayments/${repayment.id}`, {
                method: 'DELETE',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                window.scrollTo({ top: 0, behavior: "smooth" });
                setNotification({ 
                    type: "success", 
                    message: 'Repayment cancelled successfully' 
                });

                setTimeout(() => {
                    navigate('/dashboard/debt-repayments');
                }, 3000);

            } else if (response.status === 401) {
                navigate("/logout");
            } else if (response.status === 404) {
                setNotification({ type: "danger", message: 'Repayment not found.' });
            } else {
                const errorData = await response.json();
                setNotification({
                    type: "danger",
                    message: errorData.message || "Failed to cancel repayment",
                });
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (error) {
            setNotification({ 
                message: "An error occurred while cancelling the repayment.", 
                type: "danger" 
            });
            console.error("Error:", error);
        } finally {
            setIsCancelling(false);
            setIsConfirmDialogOpen(false);
        }
    };

    const cancelDeleteRepayment = () => {
        setIsConfirmDialogOpen(false);
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    if (loading) return <div className="container"><p className="has-text-centered py-6">Loading repayment details...</p></div>;
    if (error) return <div className="container"><p className="has-text-centered has-text-danger py-6">{error}</p></div>;

    return (
        <div className="container">
            <div className="section">
                <div className="level">
                    <div className="level-left">
                        <h2 className="title is-3">Debt Repayment Details</h2>
                    </div>
                    <div className="level-right">
                        <Link className="button is-light" to="/dashboard/debt-repayments">
                            <span className="icon">
                                <i className="fas fa-arrow-left"></i>
                            </span>
                            <span>Back to Repayments</span>
                        </Link>
                    </div>
                </div>

                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                {repayment && (
                    <div className="box">
                        {/* Confirmation Dialog */}
                        <ConfirmDialog
                            isOpen={isConfirmDialogOpen}
                            theme="is-danger"
                            title="Cancel Repayment"
                            message={`Are you sure you want to cancel this repayment of KES ${repayment.amount_paid} made by ${repayment.farmer?.first_name} ${repayment.farmer?.last_name}? This action cannot be undone.`}
                            onConfirm={confirmCancelRepayment}
                            onCancel={cancelDeleteRepayment}
                            isLoading={isCancelling}
                        />

                        {/* Farmer Information Card */}
                        <div className="card mb-5">
                            <div className="card-content">
                                <div className="media">
                                    <div className="media-content">
                                        <p className="title is-4">
                                            <Link 
                                                to={`/dashboard/farmers/${repayment.farmer.id}/details`} 
                                                className="no_deco_link"
                                            >
                                                {repayment.farmer.first_name} {repayment.farmer.middle_name} {repayment.farmer.last_name}
                                            </Link>
                                        </p>
                                        <p className="subtitle is-6">
                                            Farmer #: {repayment.farmer.farmer_number}
                                        </p>
                                    </div>
                                </div>
                                <div className="content">
                                    <div className="field is-horizontal">
                                        <div className="field-label is-normal">
                                            <label className="label">Phone</label>
                                        </div>
                                        <div className="field-body">
                                            <div className="field">
                                                <p className="control">{repayment.farmer.phone_number}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="field is-horizontal">
                                        <div className="field-label is-normal">
                                            <label className="label">ID Number</label>
                                        </div>
                                        <div className="field-body">
                                            <div className="field">
                                                <p className="control">{repayment.farmer.id_number}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Repayment Information Card */}
                        <div className="card">
                            <div className="card-content">
                                <h3 className="title is-5">Repayment Information</h3>
                                <div className="columns">
                                    <div className="column is-three-quarters">
                                        <div className="field is-horizontal">
                                            <div className="field-label is-normal">
                                                <label className="label">Amount Paid</label>
                                            </div>
                                            <div className="field-body">
                                                <div className="field">
                                                    <p className="control has-text-weight-bold is-size-4">
                                                        KES {parseFloat(repayment.amount_paid).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="field is-horizontal">
                                            <div className="field-label is-normal">
                                                <label className="label">Payment Date</label>
                                            </div>
                                            <div className="field-body">
                                                <div className="field">
                                                    <p className="control">
                                                        {formatToDayMonthYear(repayment.payment_date)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="field is-horizontal">
                                            <div className="field-label is-normal">
                                                <label className="label">Received By</label>
                                            </div>
                                            <div className="field-body">
                                                <div className="field">
                                                    <p className="control">
                                                        {repayment.received_by?.name || "N/A"}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="field is-horizontal">
                                            <div className="field-label is-normal">
                                                <label className="label">Status</label>
                                            </div>
                                            <div className="field-body">
                                                <div className="field">
                                                    <p className="control">
                                                        <span className={`tag is-medium ${
                                                            repayment.status === 'completed' ? 'is-success' :
                                                            repayment.status === 'pending' ? 'is-warning' :
                                                            'is-danger'
                                                        }`}>
                                                            {repayment.status.toUpperCase()}
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {repayment.notes && (
                                            <div className="field is-horizontal">
                                                <div className="field-label is-normal">
                                                    <label className="label">Notes</label>
                                                </div>
                                                <div className="field-body">
                                                    <div className="field">
                                                        <p className="control">
                                                            {repayment.notes}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <hr className="my-4" />

                                        <div className="field is-horizontal">
                                            <div className="field-label is-normal">
                                                <label className="label">Created At</label>
                                            </div>
                                            <div className="field-body">
                                                <div className="field">
                                                    <p className="control">
                                                        {new Date(repayment.created_at).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right column for cancel button */}
                                    <div className="column has-text-right">
                                        <div className="is-flex is-justify-content-flex-end is-align-items-flex-end" style={{ height: '100%' }}>
                                            {canCancel(userRole) && repayment.status === 'completed' && (
                                                <button
                                                    className={`button is-danger is-outlined ${isCancelling ? 'is-loading' : ''}`}
                                                    onClick={handleCancelRepayment}
                                                    disabled={isCancelling}
                                                >
                                                    <span className="icon">
                                                        <i className="fas fa-times"></i>
                                                    </span>
                                                    <span>Cancel Repayment</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Affected Debts Card */}
                        {repayment.debts && repayment.debts.length > 0 && (
                            <div className="card mt-5">
                                <div className="card-header">
                                    <p className="card-header-title">Debts Affected by This Repayment</p>
                                </div>
                                <div className="card-content">
                                    <table className="table is-fullwidth is-striped is-hoverable">
                                        <thead>
                                            <tr>
                                                <th>Debt Serial</th>
                                                <th>Product</th>
                                                <th>Amount Repaid</th>
                                                <th>Previous Balance</th>
                                                <th>New Balance</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {repayment.debts.map((debt, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <Link 
                                                            to={`/dashboard/debts/${debt.id}/details`}
                                                            className="has-text-primary"
                                                        >
                                                            {debt.serial_number}
                                                        </Link>
                                                    </td>
                                                    <td>{debt.product?.name || "N/A"}</td>
                                                    <td className="has-text-weight-bold">
                                                        KES {parseFloat(debt.repaid_amount || 0).toLocaleString()}
                                                    </td>
                                                    <td>
                                                        KES {parseFloat((debt.balance_due || 0) + (debt.repaid_amount || 0)).toLocaleString()}
                                                    </td>
                                                    <td className={debt.balance_due <= 0 ? 'has-text-success' : ''}>
                                                        KES {parseFloat(debt.balance_due || 0).toLocaleString()}
                                                        {debt.balance_due <= 0 && (
                                                            <span className="tag is-success ml-2">Cleared</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={`tag ${
                                                            debt.status === 'Cleared' ? 'is-success' :
                                                            debt.status === 'Partially Paid' ? 'is-warning' :
                                                            'is-danger'
                                                        }`}>
                                                            {debt.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DebtRepaymentView;